import { useEffect, useState } from "react";

import { PopupTemplate } from "templates/PopupTemplate";
import * as S from "./styles";

import { ReactComponent as Agent } from "assets/SidebarIcons/peoplesafeone.svg";
import { ReactComponent as Car } from "assets/Cards/car.svg";
import { ReactComponent as Pin } from "assets/local-pin.svg";
import { getOccurrencesIdService } from "services/OccurrenceService";

import { ICoord } from "services/CordsService";
import { IOccurrence } from "interfaces/Occurrence";
import { formatOccurrenceStatus } from "utils/treatOccurrenceStatus";

import { Socket } from "socket.io-client";
import { DefaultEventsMap } from "socket.io/dist/typed-events";
import { SocketMarkerBody } from "../../Maps/GlobalMap";
import { useMap } from "react-leaflet";
import { OccurrencePopupSkeleton } from "./skeleton";
import { fireToast } from "services/sweetAlertConfig";

type Props = {
  coords: ICoord;
  onClose: () => void;
  io: Socket<DefaultEventsMap, DefaultEventsMap>;
};

function OccurrencePopup({ coords, onClose, io }: Props) {
  const [occurrence, setOccurrence] = useState<IOccurrence | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const map = useMap();

  useEffect(() => {
    fetchOccurrence();
  }, []);

  async function fetchOccurrence() {
    await getOccurrencesIdService(coords.id)
      .then((res) => {
        setOccurrence(res.item);
        setIsLoading(false);
      })
      .catch(() => {
        onError();
      });
  }

  function onError() {
    onClose();
    setOccurrence(undefined);
    fireToast(
      "Não foi possível recuperar as informações dessa ocorrência",
      4000
    );
  }

  async function fetchPolicemanLocation(id: string | undefined = undefined) {
    if (!id) return;

    io.emit("getLocation", id, (location: SocketMarkerBody) => {
      if (!location) {
        return fireToast("A localização do pronta-resposta não foi encontrada");
      }

      map.setView(
        {
          lat: location.coords.latitude,
          lng: location.coords.longitude,
        },
        13
      );
    });
  }

  function isVehicleOccurrence() {
    return occurrence?.patrimonialType === 1 ? false : true;
  }

  function copy(type: "id" | "coords" | "prId") {
    let clipboard: string = "";
    let toast: string = "Erro";

    switch (type) {
      case "id":
        clipboard = coords.id;
        toast = "ID da ocorrência copiado";
        break;
      case "coords":
        clipboard = `${coords.coords.x}, ${coords.coords.y}`;
        toast = "Coordenadas copiadas";
        break;
      case "prId":
        clipboard =
          occurrence?.policemen?.[0]?.policeman?.id || "Nenhum ID encontrado";
        toast = "ID do pronta-resposta copiado";
        break;
      default:
        break;
    }

    navigator.clipboard.writeText(clipboard);
    fireToast(toast, 2000);
  }

  if (isLoading) return <OccurrencePopupSkeleton />;

  return (
    <PopupTemplate
      title={isVehicleOccurrence() ? "Veicular" : "Patrimonial"}
      subtitle={`${coords.coords.x}, ${coords.coords.y}`}
      renderButtons={
        <div>
          <button onClick={() => copy("id")}>Copiar ID da ocorrência</button>
          <button onClick={() => copy("prId")}>
            Copiar ID do pronta-resposta
          </button>
          <button onClick={() => copy("coords")}>Copiar coordenadas</button>
        </div>
      }
      icon={isVehicleOccurrence() ? <Car /> : <Car />}
      onPopupClose={onClose}
    >
      <S.WorkingStatus>
        <S.Info>
          <Agent />
          <S.Description>
            <p>{occurrence?.policemen?.[0]?.policeman?.user?.name}</p>
          </S.Description>
        </S.Info>
        <div>
          <S.Button
            onClick={() => {
              fetchPolicemanLocation(occurrence?.policemen?.[0]?.policeman.id);
            }}
          >
            <Pin />
          </S.Button>
        </div>
      </S.WorkingStatus>

      <S.Badge>
        <span>{formatOccurrenceStatus(occurrence?.status)}</span>
      </S.Badge>
    </PopupTemplate>
  );
}

export { OccurrencePopup };
